import React, { useRef, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper, Grid, IconButton, Menu, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
// import html2canvas from 'html2canvas';
import captureScreenshot from 'utils/screenshotUtils';

// Preset colors for common emotions
const presetEmotionColors = {
  joy: { bg: '#e8f5e9', border: '#4caf50' },
  anger: { bg: '#ffebee', border: '#f44336' },
  confusion: { bg: '#e3f2fd', border: '#2196f3' },
  frustration: { bg: '#fff3e0', border: '#ff9800' },
  sadness: { bg: '#ede7f6', border: '#673ab7' }
};

// Function to generate a consistent color based on string
const stringToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate pastel colors for better visibility
  const h = Math.abs(hash % 360); // Hue
  const s = 50 + (hash % 30); // Saturation between 50-80%
  const l = 85 + (hash % 10); // Lightness between 85-95%

  return {
    bg: `hsl(${h}, ${s}%, ${l}%)`,
    border: `hsl(${h}, ${s}%, ${l - 30}%)` // Darker version for border
  };
};

// Normalize percentage value to handle both decimal and whole number formats
const normalizePercentage = (value) => {
  // If value is already in decimal form (< 1)
  if (value < 1) {
    return value * 100;
  }
  // If value is in whole number form
  return value;
};

// Get colors for emotion - use preset if available, otherwise generate
const getEmotionColors = (emotionName) => {
  return presetEmotionColors[emotionName.toLowerCase()] || stringToColor(emotionName);
};

const EmotionalAnalysis = () => {
  const emotional_analysis = useSelector((state) => state.reviewsReducer.v2Data.review_analysis_results.emotional_analysis);
  const contentRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadAsPng = () => {
    captureScreenshot({
      contentRef,
      filename: 'emotional-analysis',
      afterCapture: handleClose
    });
  };

  const convertToRTF = (data) => {
    let rtf = '{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Arial;}}\n';

    // Escape RTF characters
    const escapeRTF = (text) => {
      if (typeof text !== 'string') {
        text = JSON.stringify(text) || '';
      }
      return text.replace(/\\/g, '\\\\').replace(/{/g, '\\{').replace(/}/g, '\\}');
    };

    // Add title
    rtf += '\\b\\fs24 Emotional Tone & Churn Signals\\b0\\fs20\\par\\par\n';
    rtf += '\\i Emotional sentiment and potential churn indicators\\i0\\par\\par\n';

    // Add Emotional Tone section
    if (data.emotions) {
      rtf += '\\b\\fs20 Emotional Tone\\b0\\fs16\\par\\par\n';
      rtf += '\\trowd\\trgaph108\\trleft0\\trqc\n';
      rtf += '\\cellx1000\\cellx2000\\cellx3000\\cellx4000\\cellx5000\n';
      rtf +=
        '\\pard\\intbl\\b Emotion\\b0\\cell\\b Percentage\\b0\\cell\\b Frequency\\b0\\cell\\b Business Impact\\b0\\cell\\b Representative Quotes\\b0\\cell\\row\n';

      data.emotions.forEach((emotion) => {
        rtf += '\\trowd\\trgaph108\\trleft0\\trqc\n';
        rtf += '\\cellx1000\\cellx2000\\cellx3000\\cellx4000\\cellx5000\n';
        rtf += `\\pard\\intbl ${escapeRTF(emotion.name)}\\cell ${escapeRTF(emotion.percentage)}%\\cell ${escapeRTF(
          emotion.frequency
        )}\\cell ${escapeRTF(emotion.business_impact)}\\cell ${escapeRTF(emotion.quotes.join('\\par '))}\\cell\\row\n`;
      });
    }

    // Add Churn Signals section
    if (data.churn_signals) {
      rtf += '\\pard\\par\\par\n';
      rtf += '\\b\\fs20 Churn Signals\\b0\\fs16\\par\\par\n';
      rtf += '\\trowd\\trgaph108\\trleft0\\trqc\n';
      rtf += '\\cellx1000\\cellx2000\\cellx3000\\cellx4000\n';
      rtf +=
        '\\pard\\intbl\\b Type\\b0\\cell\\b Frequency\\b0\\cell\\b Business Impact\\b0\\cell\\b Representative Quotes\\b0\\cell\\row\n';

      data.churn_signals.forEach((signal) => {
        rtf += '\\trowd\\trgaph108\\trleft0\\trqc\n';
        rtf += '\\cellx1000\\cellx2000\\cellx3000\\cellx4000\n';
        rtf += `\\pard\\intbl ${escapeRTF(signal.type)}\\cell ${escapeRTF(signal.frequency)}\\cell ${escapeRTF(
          signal.business_impact
        )}\\cell ${escapeRTF(signal.quotes.join('\\par '))}\\cell\\row\n`;
      });
    }

    rtf += '}';
    return rtf;
  };

  const downloadAsDoc = () => {
    if (!emotional_analysis) return;

    const data = {
      emotions: Object.entries(emotional_analysis)
        .filter(([key]) => key !== 'churn_signals')
        .map(([name, data]) => ({
          name,
          percentage: (data.percentage * 100).toFixed(1),
          frequency: data.frequency,
          business_impact: data.business_impact,
          quotes: data.representative_quotes || []
        })),
      churn_signals: emotional_analysis.churn_signals
        ? Object.entries(emotional_analysis.churn_signals).map(([type, data]) => ({
            type,
            frequency: data.frequency,
            business_impact: data.severity,
            quotes: data.quotes || []
          }))
        : []
    };

    const rtfContent = convertToRTF(data);
    const blob = new Blob([rtfContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'emotional-analysis.doc';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleClose();
  };

  if (!emotional_analysis) {
    return null;
  }

  // Get all emotions except churn_signals and sort by frequency
  const emotions = Object.entries(emotional_analysis)
    .filter(([key]) => key !== 'churn_signals')
    .map(([name, data]) => ({ name, data }))
    .filter(({ data }) => data.frequency > 0) // Filter out emotions with zero frequency
    .sort((a, b) => b.data.frequency - a.data.frequency);

  // Check if we have any emotions or churn signals
  const hasEmotions = emotions.length > 0;
  const hasChurnSignals = emotional_analysis.churn_signals && Object.keys(emotional_analysis.churn_signals).length > 0;

  // Sort churn signals by frequency
  const sortedChurnSignals = hasChurnSignals
    ? Object.entries(emotional_analysis.churn_signals)
        .map(([type, data]) => ({ type, data }))
        .filter(({ data }) => data.frequency > 0) // Filter out churn signals with zero frequency
        .sort((a, b) => b.data.frequency - a.data.frequency)
    : [];

  // If no emotions and no churn signals, don't render anything
  if (!hasEmotions && !hasChurnSignals) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <Paper elevation={1} sx={{ p: 2, height: '100%', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderRadius: '16px' }} ref={contentRef}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            Emotional Tone & Churn Signals
          </Typography>
          <Box className="screenshot-controls">
            <IconButton onClick={handleClick} size="small" sx={{ height: 28 }} aria-label="download options">
              <DownloadIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  border: 1,
                  borderColor: 'secondary.light',
                  borderRadius: 1
                }
              }}
            >
              <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
              <MenuItem onClick={downloadAsDoc}>Download as DOC</MenuItem>
            </Menu>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Emotional sentiment and potential churn indicators
        </Typography>

        {hasEmotions && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Emotional Tone
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Emotion</TableCell>
                  <TableCell>Percentage</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Business Impact</TableCell>
                  <TableCell>Representative Quotes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emotions.map(({ name, data }) => {
                  const colors = getEmotionColors(name);
                  return (
                    <TableRow key={name}>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              height: '16px',
                              borderLeft: `4px solid ${colors.border}`,
                              backgroundColor: colors.bg,
                              width: `${normalizePercentage(data.percentage) * 2}px`
                            }}
                          />
                          <Typography variant="body2">{Math.round(normalizePercentage(data.percentage))}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{data.frequency}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{data.business_impact}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              p: 1,
                              bgcolor: 'background.default',
                              borderRadius: 1,
                              fontStyle: 'italic'
                            }}
                          >
                            {data.representative_quotes.map((quote, index) => (
                              <React.Fragment key={index}>
                                &quot;{quote}&quot;
                                {index < data.representative_quotes.length - 1 && <br />}
                              </React.Fragment>
                            ))}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {hasChurnSignals && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Churn Signals
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Business Impact</TableCell>
                  <TableCell>Representative Quotes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedChurnSignals.map(({ type, data }) => {
                  const colors = stringToColor(type);
                  return (
                    <TableRow key={type}>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{type.replace(/_/g, ' ')}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              height: '16px',
                              borderLeft: `4px solid ${colors.border}`,
                              backgroundColor: colors.bg,
                              width: `${Math.min(data.frequency * 2, 100)}px`
                            }}
                          />
                          <Typography variant="body2">{data.frequency}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{data.severity}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              p: 1,
                              bgcolor: 'background.default',
                              borderRadius: 1,
                              fontStyle: 'italic'
                            }}
                          >
                            {data.quotes.map((quote, index) => (
                              <React.Fragment key={index}>
                                &quot;{quote}&quot;
                                {index < data.quotes.length - 1 && <br />}
                              </React.Fragment>
                            ))}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>
    </Grid>
  );
};

export default EmotionalAnalysis;
